// components/list/ListModeChip.tsx
// The List composer's Auto / Manual picker: the Source mode chip (components/ui/ModeChip.tsx) with
// List's two positions. The choice follows the user, as a server-side preference.
import React, { useCallback } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { resolveSourceAgentMode } from '#lib/leasing/sourceAgentMode';
import { useKernelStore } from '@/kernel/kernelStores';
import { ModeChip } from '@/components/ui/ModeChip';

const LIST_AGENT_MODES = ['MANUAL', 'AUTO'] as const;
type ListAgentMode = typeof LIST_AGENT_MODES[number];

const COPY: Record<'en' | 'fr', { header: string; labels: Record<ListAgentMode, string> }> = {
  en: { header: 'Mode', labels: { MANUAL: 'Manual', AUTO: 'Auto' } },
  fr: { header: 'Mode', labels: { MANUAL: 'Manuel', AUTO: 'Auto' } },
};

export const ListModeChip: React.FC<{ disabled?: boolean }> = ({ disabled = false }) => {
  const { kernel } = useKernel();
  // studioViewMode outlives the List screen: leaving it for Source kept 'marketing', and this chip
  // sat beside Source's own Auto chip. A Source conversation is never a List one.
  const isList = useKernelStore((state) => {
    const id = state.activeChatSessionId;
    return state.studioViewMode === 'marketing' && resolveSourceAgentMode(id ? state.inventory.ChatSession.registry[id] : null) === null;
  });
  const mode = useKernelStore((state): ListAgentMode => state.userPreferences.listAgentMode === 'MANUAL' ? 'MANUAL' : 'AUTO');
  const lang = useKernelStore((state) => (state.language === 'fr' ? 'fr' : 'en'));
  const selectMode = useCallback((next: ListAgentMode) => {
    void kernel.current?.updateAndSaveUserPreference('listAgentMode', next);
    kernel.current?.logUserAction?.('List Agent Mode Changed', { mode: next });
  }, [kernel]);
  if (!isList) return null;
  return <ModeChip modes={LIST_AGENT_MODES} mode={mode} labels={COPY[lang].labels} header={COPY[lang].header} onSelect={selectMode} disabled={disabled} />;
};
